import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { ICON_CHOICES } from "@/lib/prompts";
import { PromptIcon } from "@/components/PromptList";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Wand2, Play } from "lucide-react";
import { toast } from "sonner";

const blank = { label: "", ask: "", starter: "", icon: "Sparkles", blurb: "", folder: "" };

export const CustomPromptDialog = ({ open, onOpenChange, editing, onSaved, folders = [] }) => {
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [purpose, setPurpose] = useState("");
  const [thinking, setThinking] = useState(false);
  const [sample, setSample] = useState("");
  const [preview, setPreview] = useState("");
  const [trying, setTrying] = useState(false);

  const tryIt = async () => {
    if (!form.starter.trim() || !sample.trim()) {
      toast.error("Write what MiLu should do, and a line to try it on");
      return;
    }
    setTrying(true);
    setPreview("");
    try {
      const { data } = await api.post("/prompts/custom/try", {
        starter: form.starter.trim(),
        sample: sample.trim(),
      });
      setPreview(data.reply);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not try that prompt");
    } finally {
      setTrying(false);
    }
  };

  const askMiLu = async () => {
    const want = purpose.trim();
    if (!want) {
      toast.error("Tell MiLu what the prompt is for first");
      return;
    }
    setThinking(true);
    try {
      const { data } = await api.post("/prompts/custom/suggest", { purpose: want });
      setForm((f) => ({ ...f, ...data }));
      toast.success("MiLu wrote it — change anything you like");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not write that prompt");
    } finally {
      setThinking(false);
    }
  };

  useEffect(() => {
    setForm(editing ? { ...blank, ...editing } : blank);
  }, [editing, open]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    if (!form.label.trim() || !form.ask.trim() || !form.starter.trim()) {
      toast.error("A name, a question and an instruction are all needed");
      return;
    }
    setBusy(true);
    try {
      const body = {
        label: form.label.trim(),
        ask: form.ask.trim(),
        starter: form.starter.trim(),
        icon: form.icon,
        blurb: form.blurb.trim(),
        folder: (form.folder || "").trim(),
      };
      const { data } = editing
        ? await api.put(`/prompts/custom/${editing.id}`, body)
        : await api.post("/prompts/custom", body);
      toast.success(editing ? `${data.label} saved` : `${data.label} added to your prompts`);
      onSaved(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that prompt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-panel sm:max-w-lg" data-testid="custom-prompt-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold tracking-tighter">
            {editing ? "Edit my prompt" : "New prompt of my own"}
          </DialogTitle>
        </DialogHeader>

        {!editing && (
          <div className="rounded-2xl border border-brand/25 bg-brand/5 p-4" data-testid="prompt-idea-box">
            <p className="text-[11px] uppercase tracking-widest text-brand">Not sure what to write?</p>
            <p className="mt-1 text-xs text-ink-soft">
              Say what you want it for and MiLu fills in the whole prompt.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <input
                data-testid="prompt-idea-input"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && askMiLu()}
                maxLength={400}
                placeholder="e.g. polite messages to my landlord about repairs"
                className="min-h-[44px] flex-1 rounded-xl border border-white/10 bg-page px-4 text-sm outline-none placeholder:text-ink-soft focus:border-brand/50"
              />
              <Button
                data-testid="prompt-idea-button"
                onClick={askMiLu}
                disabled={thinking}
                variant="outline"
                className="min-h-[44px] rounded-full border-brand/40 px-5 text-sm text-brand hover:bg-brand/10"
              >
                {thinking ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Wand2 className="me-2 h-4 w-4" />}
                Write it for me
              </Button>
            </div>
          </div>
        )}

        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-brand">Name</span>
          <input
            data-testid="custom-prompt-label"
            value={form.label}
            onChange={set("label")}
            maxLength={40}
            placeholder="e.g. Fridge Chef"
            className="mt-1.5 min-h-[44px] w-full rounded-xl border border-white/10 bg-page px-4 text-sm outline-none placeholder:text-ink-soft focus:border-brand/50"
          />
        </label>

        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-brand">One-line description</span>
          <input
            data-testid="custom-prompt-blurb"
            value={form.blurb}
            onChange={set("blurb")}
            maxLength={80}
            placeholder="e.g. Dinner from whatever is left"
            className="mt-1.5 min-h-[44px] w-full rounded-xl border border-white/10 bg-page px-4 text-sm outline-none placeholder:text-ink-soft focus:border-brand/50"
          />
        </label>

        <div>
          <span className="text-[11px] uppercase tracking-widest text-brand">Folder</span>
          <input
            data-testid="custom-prompt-folder"
            value={form.folder || ""}
            onChange={set("folder")}
            maxLength={24}
            placeholder="e.g. Work, Home, Fun — leave empty for none"
            className="mt-1.5 min-h-[44px] w-full rounded-xl border border-white/10 bg-page px-4 text-sm outline-none placeholder:text-ink-soft focus:border-brand/50"
          />
          {folders.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="custom-prompt-folder-chips">
              {folders.map((f) => (
                <button
                  key={f}
                  type="button"
                  data-testid={`custom-prompt-folder-${f}`}
                  onClick={() => setForm((prev) => ({ ...prev, folder: f }))}
                  className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                    form.folder === f
                      ? "border-brand bg-brand/15 text-brand"
                      : "border-white/10 text-ink-soft hover:border-brand/50 hover:text-brand"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          )}
        </div>

        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-brand">What it asks you</span>
          <input
            data-testid="custom-prompt-ask"
            value={form.ask}
            onChange={set("ask")}
            maxLength={300}
            placeholder="e.g. What is in your fridge?"
            className="mt-1.5 min-h-[44px] w-full rounded-xl border border-white/10 bg-page px-4 text-sm outline-none placeholder:text-ink-soft focus:border-brand/50"
          />
        </label>

        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-brand">What MiLu should do</span>
          <Textarea
            data-testid="custom-prompt-starter"
            value={form.starter}
            onChange={set("starter")}
            rows={4}
            maxLength={2000}
            placeholder="e.g. Suggest three dinners I can cook from these ingredients, with steps and times: "
            className="mt-1.5 min-h-[96px] w-full resize-y rounded-xl text-sm"
          />
          <span className="mt-1 block text-xs text-ink-soft">
            This is sent quietly with your first message, so MiLu knows the job.
          </span>
        </label>

        <div>
          <span className="text-[11px] uppercase tracking-widest text-brand">Icon</span>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="custom-prompt-icons">
            {ICON_CHOICES.map((name) => (
              <button
                key={name}
                type="button"
                data-testid={`custom-prompt-icon-${name}`}
                onClick={() => setForm((f) => ({ ...f, icon: name }))}
                aria-label={name}
                className={`grid h-10 w-10 place-items-center rounded-xl border transition-colors ${
                  form.icon === name
                    ? "border-brand bg-brand/15 text-brand"
                    : "border-white/10 text-ink-soft hover:border-brand/50 hover:text-brand"
                }`}
              >
                <PromptIcon name={name} className="h-4 w-4" />
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-page p-4" data-testid="prompt-try-box">
          <p className="text-[11px] uppercase tracking-widest text-brand">Try it before saving</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <input
              data-testid="prompt-try-input"
              value={sample}
              onChange={(e) => setSample(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && tryIt()}
              maxLength={1500}
              placeholder="Type what you would send it, e.g. rice, two eggs, tinned tomatoes"
              className="min-h-[44px] flex-1 rounded-xl border border-white/10 bg-panel px-4 text-sm outline-none placeholder:text-ink-soft focus:border-brand/50"
            />
            <Button
              data-testid="prompt-try-button"
              onClick={tryIt}
              disabled={trying}
              variant="outline"
              className="min-h-[44px] rounded-full px-5 text-sm"
            >
              {trying ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Play className="me-2 h-4 w-4" />}
              Try it
            </Button>
          </div>
          {preview && (
            <p
              dir="auto"
              data-testid="prompt-try-preview"
              className="mt-3 max-h-56 overflow-y-auto whitespace-pre-wrap border-s-2 border-brand/40 ps-3 text-sm text-ink-soft"
            >
              {preview}
            </p>
          )}
        </div>

        <Button
          data-testid="custom-prompt-save"
          onClick={save}
          disabled={busy}
          className="mt-2 min-h-[48px] w-full rounded-full bg-brand text-white hover:bg-brand-hover active:scale-95"
        >
          {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : null}
          {editing ? "Save changes" : "Add to my prompts"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
